import { AutoRollToggle } from "./Rewards.js";
import { useState } from "react";
import { DIE_SIDES, checkLabel, combineModes, describeRoll, naturalD20, parseFormula, type DiceRoll, type RollInput, type RollMode, type RollRequest } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";
import { ThrowTray, useHandDice } from "./DiceThrow.js";

export function ModeToggle({ mode, onChange }: { mode: RollMode; onChange: (m: RollMode) => void }) {
  return (
    <div className="segmented" role="group" aria-label="Advantage">
      {(["disadvantage", "normal", "advantage"] as const).map((m) => (
        <button key={m} type="button" className={mode === m ? "on" : ""} onClick={() => onChange(m)}>
          {m === "normal" ? "Normal" : m === "advantage" ? "Adv" : "Dis"}
        </button>
      ))}
    </div>
  );
}

/** Rolls on the server and reports errors through the game context. */
export function useRoller() {
  const { campaign, fail } = useGame();
  const [busy, setBusy] = useState(false);
  const roll = async (input: RollInput): Promise<DiceRoll | null> => {
    setBusy(true);
    try {
      return await api.roll(campaign.id, input);
    } catch (err) {
      fail(err);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { roll, busy };
}

export function DiceTray() {
  const { myCharacter } = useGame();
  const { roll, busy } = useRoller();
  const [count, setCount] = useState(1);
  const [modifier, setModifier] = useState(0);
  const [mode, setMode] = useState<RollMode>("normal");
  const [label, setLabel] = useState("");
  const [last, setLast] = useState<DiceRoll | null>(null);
  const [hand, setHand] = useHandDice();
  const [armed, setArmed] = useState(20);

  const rollDie = async (sides: number) => {
    const r = await roll({ sides, count, modifier, mode: sides === 20 && count === 1 ? mode : "normal", label: label.trim() || undefined });
    if (r) setLast(r);
    return r;
  };

  return (
    <section className="panel">
      <h3>Dice {myCharacter && <span className="muted small">rolling as {myCharacter.name}</span>}</h3>
      {myCharacter && <AutoRollToggle />}
      <div className="dice-buttons">
        {DIE_SIDES.map((s) => (
          <button key={s} className={`die ${hand && armed === s ? "armed" : ""}`} disabled={busy} onClick={() => (hand ? setArmed(s) : void rollDie(s))}>
            d{s}
          </button>
        ))}
      </div>
      <div className="dice-options">
        <label>
          Count
          <input type="number" min={1} max={20} value={count} onChange={(e) => setCount(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} />
        </label>
        <label>
          Modifier
          <input type="number" min={-50} max={50} value={modifier} onChange={(e) => setModifier(Math.min(50, Math.max(-50, Number(e.target.value) || 0)))} />
        </label>
        <label className="grow">
          Label
          <input type="text" placeholder="e.g. Longsword attack" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
      </div>
      <div className="dice-options">
        <span className="muted small">d20:</span>
        <ModeToggle mode={mode} onChange={setMode} />
        <label className="small hand-toggle">
          <input type="checkbox" checked={hand} onChange={(e) => setHand(e.target.checked)} /> Throw by hand
        </label>
      </div>
      {hand && (
        <ThrowTray
          sides={armed}
          count={count}
          extra={armed === 20 && count === 1 && mode !== "normal" ? 1 : 0}
          disabled={busy}
          onThrow={() => rollDie(armed)}
        />
      )}
      {last && (
        <div className="last-roll">
          <RollDice roll={last} />
          <span className="total">{last.total}</span>
        </div>
      )}
    </section>
  );
}

function CritBadge({ roll }: { roll: DiceRoll }) {
  const n = naturalD20(roll);
  if (n === 1) return <span className="badge crit-fail" title="Natural 1 on the kept d20">Critical fail!</span>;
  if (n === 20) return <span className="badge crit" title="Natural 20 on the kept d20">Critical!</span>;
  return null;
}

function RollDice({ roll }: { roll: DiceRoll }) {
  return <><RollDiceInner roll={roll} /> <CritBadge roll={roll} /></>;
}

function RollDiceInner({ roll }: { roll: DiceRoll }) {
  const nat = roll.sides === 20 && roll.count === 1 ? roll.dice[0] : undefined;
  if (roll.mode !== "normal" && roll.dropped.length) {
    // Both d20s, e.g. "1d20+3 dis  Disadvantage: 14, 6 -> 6 + 3 = 9"
    return (
      <span className="roll-dice">
        <span className="formula">{roll.formula}</span>{" "}
        <span className={nat === 20 ? "nat20" : nat === 1 ? "nat1" : ""}>{describeDiceOnly(roll)}</span>
      </span>
    );
  }
  return (
    <span className="roll-dice">
      <span className="formula">{roll.formula}</span>{" "}
      [
      {roll.dice.map((d, i) => (
        <span key={i} className={nat === 20 ? "nat20" : nat === 1 ? "nat1" : ""}>
          {i > 0 ? ", " : ""}
          {d}
        </span>
      ))}
      {roll.dropped.map((d, i) => (
        <s key={`x${i}`} className="muted">
          , {d}
        </s>
      ))}
      ]
    </span>
  );
}

export function RollLog() {
  const { rolls, live } = useGame();
  return (
    <section className="panel roll-log">
      <h3>
        Roll log <span className={`dot ${live ? "live" : ""}`} title={live ? "Live" : "Reconnecting…"} />
      </h3>
      {rolls.length === 0 && <p className="muted">No rolls yet.</p>}
      <ol className="scroll-area">
        {rolls.map((r) => (
          <li key={r.id} className={r.userDiscordId === null ? "dm" : ""}>
            <time>{new Date(r.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</time>
            <span className="who" title={[r.characterName ?? r.rollerName, r.label].filter(Boolean).join(" · ")}>
              <strong>{r.characterName ?? r.rollerName}</strong>
              {r.characterName && r.userDiscordId && <span className="muted small"> {r.rollerName}</span>}
              {r.label && <span className="label"> {r.label}</span>}
            </span>
            <RollDice roll={r} />
            <span className="total">{r.total}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Prompts for rolls the DM asked for (yours, or the whole table's). */
export function RollPrompts() {
  const { requests, rolls, user, isHost, campaign, myCharacter, fail } = useGame();
  const { roll, busy } = useRoller();
  const answered = new Set(rolls.filter((r) => r.userDiscordId === user.discordId && r.requestId).map((r) => r.requestId));
  const mine = requests.filter((q) => (q.targetDiscordId === user.discordId || q.targetDiscordId === null) && !answered.has(q.id));
  const others = isHost ? requests.filter((q) => q.targetDiscordId && q.targetDiscordId !== user.discordId) : [];
  if (!mine.length && !others.length) return null;

  const answer = async (q: RollRequest) => {
    const input: RollInput = { requestId: q.id, mode: q.mode, label: q.label };
    if (q.check) input.check = q.check;
    else if (q.formula) {
      const spec = parseFormula(q.formula);
      if (spec) Object.assign(input, { sides: spec.sides, count: spec.count, modifier: spec.modifier, mode: combineModes(q.mode, spec.mode) });
    } else input.sides = 20;
    await roll(input);
  };
  const dismiss = (q: RollRequest) => api.dismissRequest(campaign.id, q.id).catch(fail);
  const needsSheet = (q: RollRequest) => !!q.check && !myCharacter;

  return (
    <section className="prompts">
      {mine.map((q) => (
        <div key={q.id} className="prompt">
          <span>
            🎲 The DM asks {q.targetDiscordId ? "you" : "everyone"} for <strong>{q.label}</strong>
            {q.check && checkLabel(q.check) !== q.label && <span className="muted"> ({checkLabel(q.check)})</span>}
            {q.mode !== "normal" && <em> with {q.mode}</em>}
            {q.formula && !q.check && <span className="muted"> ({q.formula})</span>}
          </span>
          {needsSheet(q) ? (
            <a href="#/wizard">🧙 Create a character to roll</a>
          ) : (
            <button className="primary" disabled={busy} onClick={() => answer(q)}>
              Roll
            </button>
          )}
          {q.targetDiscordId && <button onClick={() => dismiss(q)}>Dismiss</button>}
          {!q.targetDiscordId && isHost && <button onClick={() => dismiss(q)}>Close</button>}
        </div>
      ))}
      {others.map((q) => (
        <div key={q.id} className="prompt waiting">
          <span className="muted">
            Waiting for {q.targetName}: {q.label}
            {q.dc ? ` (DC ${q.dc})` : ""}
          </span>
          <button onClick={() => dismiss(q)}>Cancel</button>
        </div>
      ))}
    </section>
  );
}

/** The dice part without the crit tag (the badge shows that). */
function describeDiceOnly(roll: DiceRoll): string {
  return describeRoll(roll).replace(/ - NATURAL (1|20) - critical( fail)?$/, "");
}
